import {
    Component, OnInit, Input, HostBinding, ViewChild, OnDestroy, AfterViewInit, inject,
    ChangeDetectionStrategy
} from '@angular/core';
import {
    BehaviorSubject, Observable, Subject, of, takeUntil
} from 'rxjs';
import { RouterOutlet, Router } from '@angular/router';

import { IBladeComponent } from '../../models/blade-component';
import { BladeService } from '../../services/blade.service';

import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-blade',
    templateUrl: './blade.component.html',
    styleUrls: ['./blade.component.scss'],
    providers: [RouterOutlet],
    imports: [
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        RouterOutlet,
        SpinnerComponent,
        AsyncPipe
    ],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class BladeComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() name: string;
    @Input() bladeIndex: number;

    @HostBinding('class.closed') bladeIsClosed = true;
    @ViewChild('outlet') outlet: RouterOutlet;

    public hasParents: boolean;
    public title$: Observable<string | undefined>;

    private readonly _title$: BehaviorSubject<string | undefined> = new BehaviorSubject(undefined);
    private readonly _destroyTitle$ = new Subject<void>();
    private readonly _destroy$ = new Subject<void>();
    private _isVisible = false;
    private _componentInstance: IBladeComponent;

    private readonly _router = inject(Router);
    private readonly _bladeService = inject(BladeService);

    constructor() {
        this.title$ = this._title$.asObservable();
    }

    public get loading$(): Observable<boolean> {
        if (this._componentInstance?.childComponent?.isLoading$) {
            return this._componentInstance.childComponent.isLoading$;
        }

        if (this._componentInstance?.isLoading$) {
            return this._componentInstance.isLoading$;
        }

        return of(false);
    }

    public get isLoadingExtra$(): Observable<boolean> {
        if (this._componentInstance?.childComponent?.isLoadingExtra$) {
            return this._componentInstance.childComponent.isLoadingExtra$;
        }

        if (this._componentInstance?.isLoadingExtra$) {
            return this._componentInstance.isLoadingExtra$;
        }

        return of(false);
    }

    //#region if blade should be visible
    set isVisible(visible: boolean) {
        this._isVisible = visible;
        this.bladeIsClosed = !visible;
    }
    get isVisible() {
        return this._isVisible;
    }
    //#endregion

    async ngOnInit() {
        this.hasParents = this.bladeIndex > 0;
    }

    ngAfterViewInit() {
        this._bladeService.addOutlet(this.outlet);

        this.outlet.activateEvents.pipe(
            takeUntil(this._destroy$)
        ).subscribe(() => {
            this._updateBlade();
        });

        this.outlet.deactivateEvents.pipe(
            takeUntil(this._destroy$)
        ).subscribe(() => {
            this._updateBlade();
        });

        // Defer to next tick to avoid ExpressionChangedAfterItHasBeenCheckedError                                               │
        setTimeout(() => this._updateBlade(), 0);
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();

        if (this.outlet) {
            this.outlet.ngOnDestroy();
            this._bladeService.removeOutlet(this.outlet, this.name);
        }

        this._destroyTitle$.next();
        this._destroyTitle$.complete();
    }

    public close(): void {
        let outlet: { [key: string]: any } = {};
        outlet[this.outlet.name] = null
        this._title$.next(undefined);
        this._destroyTitle$.next();
        this._router.navigate(['', { outlets: outlet }]);
    }

    private async _updateBlade() {
        this.isVisible = this.outlet.isActivated;
        this.bladeIsClosed = !this.isVisible;

        if (this.isVisible) {
            this._componentInstance = this.outlet.component as any;
            
            // Reset title and unsubscribe from previous title subscriptions when activating
            this._destroyTitle$.next();
            this._title$.next(undefined);
              
            // For right-side blades, leave bladeIndex undefined so the component's
            // inner blade-header (with icon) is visible instead of the wrapper header.
            if (!this.name?.startsWith('outlet-right')) {
                this._componentInstance.bladeIndex = this.bladeIndex;
            }
            if (this._componentInstance.title$) {
                this._componentInstance.title$.pipe(
                    takeUntil(this._destroyTitle$)
                ).subscribe((title) => {
                    this._title$.next(title);
                });
            } else {
                this._title$.next(this._componentInstance.title);
            }

        } else {
            this._componentInstance = undefined;
        }
    }
}
